import React, { useState, useContext, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUserPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseTimeParts = (timeStr) => {
  if (!timeStr) return [0, 0];
  const parts = timeStr.split(':');
  return [parseInt(parts[0], 10) || 0, parseInt(parts[1], 10) || 0];
};

const getEarliestStartDate = (frequencyType, reportTime) => {
  const now = new Date();
  const todayStr = toDateInputValue(now);

  if (frequencyType !== 'daily' || !reportTime) {
    return todayStr;
  }

  const [hours, minutes] = parseTimeParts(reportTime);
  const reportToday = new Date();
  reportToday.setHours(hours, minutes, 0, 0);

  if (reportToday <= now) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toDateInputValue(tomorrow);
  }

  return todayStr;
};

const formatStartLabel = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  return date.toLocaleDateString();
};

const GoalCreationForm = ({ show, onClose, refreshGoals }) => {
  const { user } = useContext(MyContext);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [frequencyType, setFrequencyType] = useState('daily');
  const [frequencyValue, setFrequencyValue] = useState('');
  const [reportTime, setReportTime] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isOngoing, setIsOngoing] = useState(false);
  const [partners, setPartners] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!show) return;
    const earliest = getEarliestStartDate(frequencyType, reportTime);
    setStartDate((current) => {
      if (!current || current < earliest) {
        return earliest;
      }
      return current;
    });
  }, [show, frequencyType, reportTime]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/search_partner.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery, exclude_id: user.user_id })
      });
      
      const data = await response.json();
      console.log('Search results:', data);
      if (data.success) {
        setSearchResults(data.users);
      } else {
        setError(data.message || 'Failed to search users');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addPartner = (user) => {
    if (partners.length >= 2) {
      setError('You can only add up to 2 partners');
      return;
    }
    if (!partners.some(p => p.id === user.id)) {
      setPartners([...partners, user]);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const removePartner = (id) => {
    setPartners(partners.filter(p => p.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!title || !frequencyType || !reportTime) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (frequencyType === 'weekly' && !frequencyValue) {
      setError('Please select at least one day for weekly frequency');
      return;
    }
    
    if (frequencyType === 'monthly' && !frequencyValue) {
      setError('Please select a day of month for monthly frequency');
      return;
    }
    
    if (!isOngoing && !endDate) {
      setError('Please select an end date or mark as ongoing');
      return;
    }

    const earliestStart = getEarliestStartDate(frequencyType, reportTime);
    const effectiveStartDate = !startDate || startDate < earliestStart ? earliestStart : startDate;
    
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/create_goal.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id,
          title,
          description,
          frequency_type: frequencyType,
          frequency_value: frequencyValue,
          report_time: reportTime,
          start_date: effectiveStartDate,
          end_date: isOngoing ? null : endDate,
          partners: partners.map(p => p.id)
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSuccess('Goal created successfully!');
        refreshGoals();
        setTimeout(() => {
          onClose();
          setSuccess(null);
          resetForm();
        }, 1500);
      } else {
        setError(data.message || data.messages || 'Failed to create goal');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setFrequencyType('daily');
    setFrequencyValue('');
    setReportTime('');
    setStartDate('');
    setEndDate('');
    setIsOngoing(false);
    setPartners([]);
    setError(null);
  };

  const earliestStartDate = getEarliestStartDate(frequencyType, reportTime);
  const firstReportDate = !startDate || startDate < earliestStartDate ? earliestStartDate : startDate;
  const startsToday = firstReportDate === toDateInputValue(new Date());

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Goal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Goal Title *</Form.Label>
            <Form.Control 
              type="text" 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What do you want to achieve?"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add more details about your goal"
            />
          </Form.Group>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Reporting Frequency *</Form.Label>
                <Form.Select 
                  value={frequencyType}
                  onChange={(e) => setFrequencyType(e.target.value)}
                  required
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Report Time *</Form.Label>
                <Form.Control 
                  type="time" 
                  value={reportTime}
                  onChange={(e) => setReportTime(e.target.value)}
                  required
                />
                {frequencyType === 'daily' && reportTime && (
                  <Form.Text className="text-muted">
                    Daily goals start today if this time is still ahead; otherwise they start tomorrow.
                    First report: {startsToday ? 'today' : formatStartLabel(firstReportDate)} at {reportTime}.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          
          {frequencyType === 'weekly' && (
            <Form.Group className="mb-3">
              <Form.Label>Select Days *</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                  <Form.Check
                    key={day}
                    type="checkbox"
                    id={`day-${index}`}
                    label={day}
                    checked={frequencyValue.includes(index.toString())}
                    onChange={(e) => {
                      const days = frequencyValue.split(',').filter(d => d !== '');
                      if (e.target.checked) {
                        days.push(index.toString());
                      } else {
                        const idx = days.indexOf(index.toString());
                        if (idx > -1) days.splice(idx, 1);
                      }
                      setFrequencyValue(days.join(','));
                    }}
                  />
                ))}
              </div>
            </Form.Group>
          )}
          
          {frequencyType === 'monthly' && (
            <Form.Group className="mb-3">
              <Form.Label>Day of Month *</Form.Label>
              <Form.Control 
                type="number" 
                min="1" 
                max="31"
                value={frequencyValue}
                onChange={(e) => setFrequencyValue(e.target.value)}
                placeholder="e.g. 15 (for 15th of each month)"
                required
              />
            </Form.Group>
          )}
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Start Date *</Form.Label>
                <Form.Control 
                  type="date" 
                  value={startDate}
                  min={earliestStartDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
                {frequencyType === 'daily' && (
                  <Form.Text className="text-muted">
                    Earliest start is {startsToday ? 'today' : 'tomorrow'} because of the report time.
                    You can pick a later date.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isOngoing}
                    required={!isOngoing}
                  />
                  <Form.Check
                    type="checkbox"
                    id="ongoing-check"
                    label="Ongoing"
                    checked={isOngoing}
                    onChange={(e) => setIsOngoing(e.target.checked)}
                    className="ms-2"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Accountability Partners (Max 2)</Form.Label>
            <div className="d-flex mb-2">
              <Form.Control 
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by username"
              />
              <Button 
                variant="primary" 
                onClick={handleSearch}
                disabled={!searchQuery.trim()}
                className="ms-2"
              >
                Search
              </Button>
            </div>
            
            {loading && searchResults.length === 0 && <p>Searching...</p>}
            
            {searchResults.length > 0 && (
              <div className="mb-3">
                <label className="mb-2">Search Results:</label>
                <div className="list-group">
                  {searchResults.map(user => (
                    <div 
                      key={user.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong>{user.username}</strong>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        onClick={() => addPartner(user)}
                        disabled={partners.some(p => p.id === user.id)}
                      >
                        <FontAwesomeIcon icon={faUserPlus} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {partners.length > 0 && (
              <div>
                <h6>Selected Partners:</h6>
                <div className="list-group">
                  {partners.map(user => (
                    <div 
                      key={user.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <strong>{user.username}</strong> ({user.email})
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline-danger"
                        onClick={() => removePartner(user.id)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Form.Group>
          
          <div className="d-flex justify-content-end">
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Goal'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default GoalCreationForm;
